import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { useLanguage } from './language-context';
import { Sheet } from './sheet';
import './confirm-with-reason.css';

export interface ReasonOption {
  code: string;
  label: string;
}

export interface ReasonResult {
  code: string | null;
  text: string;
}

// Confirm-with-reason (CB-17): destructive or moderated actions (reject a payment
// proof with one of the DR-46 reason codes, cancel a booking, take an offer down).
// Optional reason codes render as a radio list; an optional free-text note follows.
// `requireReason` blocks the confirm until a code is picked (when codes are given)
// or text is entered (when they are not). All visible copy comes from the caller
// as already-translated strings — the dialog owns behaviour, not vocabulary.
export function isReasonComplete(
  result: ReasonResult,
  { requireReason, hasCodes }: { requireReason: boolean; hasCodes: boolean },
): boolean {
  if (!requireReason) return true;
  return hasCodes ? result.code !== null : result.text.trim().length > 0;
}

export function ConfirmWithReason({
  open,
  title,
  body,
  reasons,
  reasonLabel,
  notePlaceholder,
  showNote = true,
  requireReason = false,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body?: ReactNode;
  reasons?: readonly ReasonOption[];
  reasonLabel?: string;
  notePlaceholder?: string;
  showNote?: boolean;
  requireReason?: boolean;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: (result: ReasonResult) => void;
  onCancel: () => void;
}) {
  const { t } = useLanguage();
  const groupId = useId();
  const [code, setCode] = useState<string | null>(null);
  const [text, setText] = useState('');

  // A fresh dialog every time it opens.
  useEffect(() => {
    if (open) {
      setCode(null);
      setText('');
    }
  }, [open]);

  const hasCodes = Boolean(reasons && reasons.length > 0);
  const result: ReasonResult = { code, text: text.trim() };
  const ready = isReasonComplete(result, { requireReason, hasCodes });

  return (
    <Sheet
      open={open}
      onClose={onCancel}
      title={title}
      role="alertdialog"
      width={440}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            {cancelLabel ?? t('action.cancel')}
          </button>
          <button
            type="button"
            className={tone === 'danger' ? 'btn btn--danger' : 'btn'}
            disabled={!ready || busy}
            onClick={() => onConfirm(result)}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {body && <div className="confirm-reason__body">{body}</div>}
      {hasCodes && (
        <fieldset className="confirm-reason__codes">
          {reasonLabel && <legend className="confirm-reason__legend">{reasonLabel}</legend>}
          {reasons!.map((r) => (
            <label
              key={r.code}
              className={code === r.code ? 'confirm-reason__code is-on' : 'confirm-reason__code'}
            >
              <input
                type="radio"
                name={groupId}
                value={r.code}
                checked={code === r.code}
                onChange={() => setCode(r.code)}
              />
              <span>{r.label}</span>
            </label>
          ))}
        </fieldset>
      )}
      {showNote && (
        <textarea
          className="field confirm-reason__note"
          rows={3}
          value={text}
          placeholder={notePlaceholder}
          aria-label={notePlaceholder ?? reasonLabel ?? title}
          onChange={(e) => setText(e.target.value)}
        />
      )}
    </Sheet>
  );
}
